import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { findParent } from './utils';
import { createFocusTrap } from 'focus-trap';

export default class VimeoVideoLightbox {

	constructor(element) {
		this.handleCloseClick = this.handleCloseClick.bind(this);
		this.handleTriggerClick = this.handleTriggerClick.bind(this);
		this.element = element;
		this.openClass = 'gigs-vimeo-video-lightbox--open';
		this.triggerQuery = 'a[href^="https://www.vimeo.com"]:not([rel~="external"]), a[href^="https://vimeo.com"]:not([rel~="external"])';
		this.closeButton = (this.element instanceof HTMLElement) ? this.element.querySelector('.gigs-vimeo-video-lightbox__close') : undefined;
		this.overlay = (this.element instanceof HTMLElement) ? this.element.querySelector('.gigs-vimeo-video-lightbox__overlay') : undefined;
		this.titleElement = (this.element instanceof HTMLElement) ? this.element.querySelector('.gigs-vimeo-video-lightbox__header .gigs-eyebrow__text') : undefined;
		this.externalAnchor = (this.element instanceof HTMLElement) ? this.element.querySelector('.gigs-vimeo-video-lightbox__open-external') : undefined;
		this.defaultTitle = (this.titleElement instanceof HTMLElement) ? this.titleElement.innerHTML : '';
		this.focusTrap = null;
		this.wpAdminBar = document.getElementById('wpadminbar');
		this.focusableElements = [this.element];
		if (this.wpAdminBar) this.focusableElements.push(this.wpAdminBar);
	}

	init() {
		if (this.closeButton instanceof HTMLElement) {
			this.closeButton.addEventListener('click', this.handleCloseClick);
		}
		if (this.overlay instanceof HTMLElement) {
			this.overlay.addEventListener('click', this.handleCloseClick);
		}
		this.initTriggers();
		this.initFocusTrap();
	}

	destroy() {
		if (this.closeButton instanceof HTMLElement) {
			this.closeButton.removeEventListener('click', this.handleCloseClick);
		}
		if (this.overlay instanceof HTMLElement) {
			this.overlay.removeEventListener('click', this.handleCloseClick);
		}
		this.destroyTriggers();
		this.destroyFocusTrap();
	}

	initFocusTrap() {
		this.focusTrap = createFocusTrap(this.focusableElements, {
			onDeactivate: () => this.close(),
		});
	}

	destroyFocusTrap() {
		if (this.focusTrap) {
			this.focusTrap.deactivate();
			this.focusTrap = null;
		}
	}

	initTriggers(container) {
		container = (container instanceof HTMLElement) ? container : document;
		const triggers = container.querySelectorAll(this.triggerQuery);
		triggers.forEach(trigger => {
			trigger.addEventListener('click', this.handleTriggerClick);
		});
	}

	destroyTriggers(container) {
		container = (container instanceof HTMLElement) ? container : document;
		const triggers = container.querySelectorAll(this.triggerQuery);
		triggers.forEach(trigger => {
			trigger.removeEventListener('click', this.handleTriggerClick);
		});
	}

	open() {
		if (this.element instanceof HTMLElement) {
			this.element.classList.add(this.openClass);
			this.focusTrap.activate();
			disableBodyScroll(this.element);
		}
	}

	close() {
		if (this.element instanceof HTMLElement) {
			this.removeIframe();
			this.focusTrap.deactivate();
			this.element.classList.remove(this.openClass);
			enableBodyScroll(this.element);
		}
	}

	renderIframe(url) {
		if (this.element instanceof HTMLElement) {
			const container = this.element.querySelector('.gigs-vimeo-video-lightbox__video');
			const iframe = document.createElement('iframe');
			iframe.setAttribute('src', url);
			iframe.setAttribute('frameborder', 0);
			iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
			iframe.setAttribute('allowfullscreen', true);
			container.innerHTML = '';
			container.appendChild(iframe);
		}
	}

	renderTitle(title) {
		if (this.titleElement instanceof HTMLElement) {
			if (title) {
				this.titleElement.innerHTML = title;
			} else {
				this.titleElement.innerHTML = this.defaultTitle;
			}
		}
	}

	removeIframe() {
		if (this.element instanceof HTMLElement) {
			const container = this.element.querySelector('.gigs-vimeo-video-lightbox__video');
			container.innerHTML = '';
		}
	}

	getEmbedURLFromURL(url) {
		let embedURL = '';
		if (typeof url === 'string') {
			const urlParts = url.split('?');
			const params = new URLSearchParams(urlParts[1] || '');
			params.set('autoplay', '1');
			const urlParsed = new URL(urlParts[0]);
			const isEvent = urlParsed.pathname.includes('/event/');
			const vimeoIds = urlParsed.pathname.split('/').filter((p) => (Boolean(p) && p !== 'event'));
			const vimeoId = vimeoIds[0];
			const hash = vimeoIds[1];
			let embedBase = '';
			if (isEvent) {
				embedBase = `https://www.vimeo.com/event/${vimeoId}/embed`;
				if (hash) {
					embedBase += `/${hash}`;
				}
			} else {
				embedBase = `https://player.vimeo.com/video/${vimeoId}`;
				if (hash) {
					params.set('h', hash);
				}
			}
			embedURL = `${embedBase}?${params.toString()}`;
		}
		return embedURL;
	}

	handleTriggerClick(e) {
		const { target } = e;
		const trigger = findParent(target, (element) => element.hasAttribute('href'));
		if (trigger instanceof HTMLElement) {
			e.preventDefault();
			const url = trigger.getAttribute('href');
			const embedURL = this.getEmbedURLFromURL(url);
			const title = trigger.getAttribute('title');
			this.externalAnchor.href = url;
			this.renderIframe(embedURL);
			this.renderTitle(title);
			this.open();
		}
	}

	handleCloseClick(e) {
		e.preventDefault();
		this.close();
	}
}
